import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, Landmark, Loader2, Lock } from 'lucide-react';
import { api } from '../services/api';

const BENEFITS = ['Banker leads and monitor/settings', 'IPO calendar and watch queue', 'Personalised alerts'];

export function BankerPaywall({ children }: { children?: ReactNode }) {
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.getEntitlements(),
    staleTime: 30_000,
  });

  if (isError) {
    return (
      <main className="p-4 sm:p-8 max-w-3xl mx-auto">
        <section className="rounded-2xl border border-red-400/30 bg-slate-800/90 p-6 text-center" role="alert">
          <h1 className="text-xl font-bold text-white">Unable to verify Banker access</h1>
          <p className="mt-2 text-slate-300">Try again before opening subscription-only tools.</p>
          <button type="button" onClick={() => void refetch()} className="mt-5 min-h-11 rounded-lg bg-teal-500 px-5 py-3 font-semibold text-slate-950 hover:bg-teal-400">Try again</button>
        </section>
      </main>
    );
  }
  if (isLoading || !data) {
    return <div className="p-8 flex justify-center" role="status" aria-label="Loading access"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>;
  }
  if (!data.investment_banking_gate || data.investment_banking?.active) return <>{children}</>;

  const state = data.investment_banking?.state ?? 'none';
  const isOverdue = state === 'overdue_disabled';
  const needsPaymentManagement = isOverdue || state === 'renewal_due';
  const isCancelled = state === 'cancelled';

  const startCheckout = async () => {
    setIsStarting(true);
    setCheckoutError(null);
    try {
      const response = needsPaymentManagement
        ? (api.payNowBanker ? await api.payNowBanker() : await api.manageBankerSubscription())
        : await api.createBankerCheckout();
      window.location.assign('url' in response ? response.url : response.checkout_url);
    } catch {
      setCheckoutError('We could not start checkout. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <main className="p-4 sm:p-8 max-w-3xl mx-auto" aria-labelledby="banker-paywall-title">
      <section className="rounded-2xl border border-teal-400/30 bg-slate-800/90 p-6 sm:p-8 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-teal-400/10 p-3" aria-hidden="true"><Landmark className="h-7 w-7 text-teal-300" /></div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-300">Investment Banking</p>
            <h1 id="banker-paywall-title" className="mt-1 text-2xl font-bold text-white">Unlock your Banker edge</h1>
            <p className="mt-2 text-slate-300">One add-on for the insights and alerts that help you move with conviction.</p>
          </div>
        </div>
        <div className="mt-6 flex items-end gap-2"><span className="text-3xl font-bold text-white">USD 40</span><span className="pb-1 text-slate-400">/ month · 7-day free trial</span></div>
        <ul className="mt-5 space-y-3 text-sm text-slate-200" aria-label="Included features">
          {BENEFITS.map((benefit) => <li key={benefit} className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-300" aria-hidden="true" />{benefit}</li>)}
        </ul>
        {state === 'trialing' && <p className="mt-5 text-sm text-teal-200">Your trial is active. Confirmation is based on payment events.</p>}
        {state === 'renewal_due' && <p className="mt-5 text-sm text-amber-200">Your renewal is due. Update payment to keep Banker access.</p>}
        {state === 'grandfathered' && <p className="mt-5 text-sm text-teal-200">Your grandfathered Banker access is active.</p>}
        {isOverdue && <p className="mt-5 flex items-center gap-2 text-sm text-amber-200"><AlertCircle className="h-4 w-4" aria-hidden="true" />service disabled - pay now</p>}
        {isCancelled && <p className="mt-5 text-sm text-slate-300">Your Banker subscription is cancelled. Subscribe again to restore access.</p>}
        {checkoutError && <p className="mt-4 text-sm text-red-300" role="alert">{checkoutError}</p>}
        <button type="button" onClick={startCheckout} disabled={isStarting} className="mt-7 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-500 px-5 py-3 font-semibold text-slate-950 transition hover:bg-teal-400 disabled:cursor-not-allowed disabled:opacity-60">
          {isStarting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Lock className="h-4 w-4" aria-hidden="true" />}
          {isOverdue ? 'Pay now' : state === 'renewal_due' ? 'Manage payment' : 'Subscribe'}
        </button>
      </section>
    </main>
  );
}
